/**
 * AccountsUsersService - HTTP client for the "Cuentas y Usuarios" module.
 * Encapsulates all fetch calls, auth header injection and error handling so
 * presentational components stay free of raw HTTP. Base URL is the Ink PHP API.
 */
import {
    urlInk,
    endpointAdminListAccounts,
    endpointAdminSearchAccounts,
    endpointAdminGetAccount,
    endpointAdminValidatePrefix,
    endpointAdminCreateAccount,
    endpointAdminUpdateAccount,
    endpointAdminDeactivateAccount,
    endpointAdminDeleteAccount,
    endpointAdminUpdateServices,
    endpointAdminExportAccounts,
    endpointAdminGetBilling,
    endpointAdminSetBillingData,
    endpointAdminSetBankInfo,
    endpointAdminGetChannels,
    endpointAdminGetPercentPublishers,
    endpointAdminSetPercentPublishers,
    endpointAdminDeletePercentPublishers,
    endpointAdminListUsers,
    endpointAdminGetUser,
    endpointAdminCreateUser,
    endpointAdminUpdateUser,
    endpointAdminDeleteUser,
    endpointAdminSetUserActive,
    endpointAdminAssignRole,
    endpointAdminAssignAccount,
    endpointAdminAssignPrimaryUser,
    endpointAdminNotifyUser,
    endpointAdminValidateUser,
    endpointAdminExportUsers,
    endpointPermissionsCatalog,
    endpointRolePresets,
    endpointGetMyPermissions,
    endpointListAccountUsers,
    endpointAssignAccountRole,
} from "./Endpoints";
import AuthService from "./AuthService";

class AccountsUsersService {
    async request(endpoint, options = {}, isBlob = false) {
        const token = AuthService.getToken();
        const headers = { ...(options.headers || {}) };
        if (!isBlob && !(options.body instanceof FormData)) {
            headers['Content-Type'] = 'application/json';
        }
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${urlInk}${endpoint}`, {
            method: options.method || 'GET',
            headers,
            body: options.body,
        });

        if (!response.ok) {
            throw new Error('Error de conexión con el servidor');
        }

        if (isBlob) {
            return response.blob();
        }

        let res;
        try {
            res = await response.json();
        } catch (e) {
            throw new Error('Respuesta no válida del servidor');
        }

        if (!res || res.status !== 'success') {
            const msg = (res && res.msg) ? res.msg : 'Ocurrió un error';
            const error = new Error(msg);
            error.status = res ? res.status : 'error';
            throw error;
        }

        return res.data;
    }

    buildParams(obj) {
        const params = new URLSearchParams();
        Object.keys(obj).forEach((k) => {
            const v = obj[k];
            if (v !== undefined && v !== null && v !== '') {
                params.set(k, v);
            }
        });
        return params.toString();
    }

    // ---------------- Accounts ----------------
    getAccounts(filters = {}) {
        const qs = this.buildParams({
            page: filters.page,
            per_page: filters.per_page,
            type: filters.type,
            search: filters.search,
            prefix: filters.prefix,
            service: filters.service,
            tier: filters.tier,
        });
        return this.request(`${endpointAdminListAccounts}?${qs}`);
    }

    searchAccounts({ search = '', limit = 20 } = {}) {
        const qs = this.buildParams({ search, limit });
        return this.request(`${endpointAdminSearchAccounts}?${qs}`);
    }

    getAccount(idOrPrefix) {
        const qs = typeof idOrPrefix === 'number'
            ? this.buildParams({ id_pub: idOrPrefix })
            : this.buildParams({ prefix: idOrPrefix });
        return this.request(`${endpointAdminGetAccount}?${qs}`);
    }

    validatePrefix(prefix) {
        const qs = this.buildParams({ prefix });
        return this.request(`${endpointAdminValidatePrefix}?${qs}`);
    }

    createAccount(payload) {
        return this.request(endpointAdminCreateAccount, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }

    updateAccount(payload) {
        return this.request(endpointAdminUpdateAccount, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }

    deactivateAccount(idPub) {
        return this.request(endpointAdminDeactivateAccount, {
            method: 'POST',
            body: JSON.stringify({ id_pub: idPub }),
        });
    }

    deleteAccount(idPub) {
        return this.request(endpointAdminDeleteAccount, {
            method: 'POST',
            body: JSON.stringify({ id_pub: idPub }),
        });
    }

    updateServices(payload) {
        return this.request(endpointAdminUpdateServices, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }

    async exportAccounts(filters = {}) {
        const qs = this.buildParams({
            type: filters.type,
            search: filters.search,
            prefix: filters.prefix,
            service: filters.service,
            tier: filters.tier,
        });
        return this.request(`${endpointAdminExportAccounts}?${qs}`, {}, true);
    }

    // ---------------- Billing & Bank info ----------------
    getBilling(prefix) {
        const qs = this.buildParams({ prefix });
        return this.request(`${endpointAdminGetBilling}?${qs}`);
    }

    setBillingData(payload) {
        return this.request(endpointAdminSetBillingData, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }

    setBankInfo(payload) {
        return this.request(endpointAdminSetBankInfo, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }

    // ---------------- Percent publishers ----------------
    getChannels() {
        return this.request(endpointAdminGetChannels);
    }

    getPercentPublishers(filters = {}) {
        const qs = this.buildParams({
            id_publisher: filters.id_publisher,
            id_channel: filters.id_channel,
            business_model: filters.business_model,
            content_type: filters.content_type,
            apply: filters.apply,
        });
        return this.request(`${endpointAdminGetPercentPublishers}?${qs}`);
    }

    setPercentPublishers({ publishers = [], channels = [] } = {}) {
        return this.request(endpointAdminSetPercentPublishers, {
            method: 'POST',
            body: JSON.stringify({ publishers, channels }),
        });
    }

    deletePercentPublisher(id) {
        return this.request(endpointAdminDeletePercentPublishers, {
            method: 'POST',
            body: JSON.stringify({ id }),
        });
    }

    // ---------------- Users ----------------
    getUsers(filters = {}) {
        const qs = this.buildParams({
            page: filters.page,
            per_page: filters.per_page,
            search: filters.search,
            role: filters.role,
        });
        return this.request(`${endpointAdminListUsers}?${qs}`);
    }

    getUser(id) {
        const qs = this.buildParams({ id });
        return this.request(`${endpointAdminGetUser}?${qs}`);
    }

    validateUser({ user = '', email = '', id = 0 } = {}) {
        const qs = this.buildParams({ user, email, id });
        return this.request(`${endpointAdminValidateUser}?${qs}`);
    }

    createUser(payload) {
        return this.request(endpointAdminCreateUser, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }

    updateUser(payload) {
        return this.request(endpointAdminUpdateUser, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }

    deleteUser(id) {
        return this.request(endpointAdminDeleteUser, {
            method: 'POST',
            body: JSON.stringify({ id }),
        });
    }

    // Desactiva (active: 0) o reactiva (active: 1). Reversible a proposito: el
    // borrado es la accion destructiva y ya esta en deleteUser. Se manda el estado
    // objetivo en vez de alternar el actual para que la llamada sea idempotente.
    setUserActive(id, active) {
        return this.request(endpointAdminSetUserActive, {
            method: 'POST',
            body: JSON.stringify({ id, active: active ? 1 : 0 }),
        });
    }

    assignRole(id, type) {
        return this.request(endpointAdminAssignRole, {
            method: 'POST',
            body: JSON.stringify({ id, type }),
        });
    }

    assignAccount(id, editorial) {
        return this.request(endpointAdminAssignAccount, {
            method: 'POST',
            body: JSON.stringify({ id, editorial }),
        });
    }

    assignPrimaryUser(id) {
        return this.request(endpointAdminAssignPrimaryUser, {
            method: 'POST',
            body: JSON.stringify({ id }),
        });
    }

    notifyUser(id) {
        return this.request(endpointAdminNotifyUser, {
            method: 'POST',
            body: JSON.stringify({ id }),
        });
    }

    async exportUsers(filters = {}) {
        const qs = this.buildParams({
            search: filters.search,
            role: filters.role,
        });
        return this.request(`${endpointAdminExportUsers}?${qs}`, {}, true);
    }

    // --- Roles & permissions -------------------------------------------------
    // getMyPermissions is the only trustworthy client-side source of permissions:
    // the API resolves them from the database, whereas the JWT claim is a stale
    // snapshot and is never used for authorization. Every can() check in the UI
    // must go through this, never through user.permissions.
    getMyPermissions() {
        return this.request(endpointGetMyPermissions);
    }

    getPermissionsCatalog() {
        return this.request(endpointPermissionsCatalog);
    }

    getRolePresets(code) {
        const qs = code ? `?code=${encodeURIComponent(code)}` : '';
        return this.request(`${endpointRolePresets}${qs}`);
    }

    saveRolePreset({ code, name, description, modules }) {
        return this.request(endpointRolePresets, {
            method: 'POST',
            body: JSON.stringify({ code, name, description, modules }),
        });
    }

    deleteRole(code) {
        return this.request(`${endpointRolePresets}?code=${encodeURIComponent(code)}`, {
            method: 'DELETE',
        });
    }

    listAccountUsers(filters = {}) {
        const qs = this.buildParams({
            search: filters.search,
            type: filters.type,
            editorial: filters.editorial,
        });
        return this.request(`${endpointListAccountUsers}?${qs}`);
    }

    assignAccountRole({ id, type, modules }) {
        return this.request(endpointAssignAccountRole, {
            method: 'POST',
            body: JSON.stringify({ id, type, modules }),
        });
    }
}

const AccountsUsersServiceInstance = new AccountsUsersService();

export { AccountsUsersServiceInstance as AccountsUsersService };

// Shared helper to trigger a CSV download from a blob.
export function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default AccountsUsersServiceInstance;